<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import ElementalSearchTab from './ElementalSearchTab/index.vue'
import UsersSearchTab from './UsersSearchTab/index.vue'
import TopicSearchTab from './TopicSearchTab/index.vue'
import ListSearchTab from './ListSearchTab/index.vue'
import DebouncedField from '@/components/DebouncedField/index.vue'
import { ElementalTypeEnum } from '~~/src/types/elemental'

const TeamsSearchTab = defineAsyncComponent(() => import('./TeamsSearchTab/index.vue'))

const { colors } = useTheme()
const { teams: teamsFeatures } = useFeatureFlags().value

const search = ref('')
const searchDebounced = useDebounce(search, 500)
const showFullContainer = process.server ? ref(false) : useState('header-search:show', () => false)

const containerRef = ref<HTMLDivElement>()
const fieldRef = ref<InstanceType<typeof DebouncedField> | null>(null)
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

function closeInput() {
  showFullContainer.value = false
  fieldRef.value?.blur()
}

function openInput() {
  showFullContainer.value = true
  fieldRef.value?.focus()
}

const router = useRouter()
router.beforeEach(() => {
  closeInput()
})

onClickOutside(containerRef, () => showFullContainer.value = false)

whenever(showFullContainer, () => {
  closeAllHeaderMenus()
})

const headerMenusActive = useHeaderMenusActive()
watch(headerMenusActive, () => {
  const hasMenuActive = Object.values(headerMenusActive.value).some(active => active)

  if (hasMenuActive)
    closeInput()
}, {
  deep: true,
})

useEventListener('keydown', (event) => {
  if (event.key.toLowerCase() === 'escape') {
    fieldRef.value?.reset()
    closeInput()
    event.preventDefault()
  }
  if ((event.key === 'k' && event.ctrlKey)
 || (event.key === 'k' && event.metaKey)) {
    event.preventDefault()
    if (fieldRef.value)
      fieldRef.value?.focus()

    openInput()
  }
})

const handlerFocus = () => {
  showFullContainer.value = true
  openInput()
}

const handlerClose = () => {
  showFullContainer.value = false
  closeInput()
}
</script>

<template>
  <div
    ref="containerRef"
    w-full h="40px"
    duration-125
    relative
  >
    <div
      absolute left-0 z-99
      w-full rounded-2 duration-125
      flex="~ col" gap-2
      :class="showFullContainer && [
        $style.container,
        (isMobile ? $style.containerMobileActive : $style.containerDesktopActive),
      ]"
    >
      <DebouncedField
        ref="fieldRef"
        v-model="search"
        :placeholder="showFullContainer ? (isMobile ? 'Search anything' : 'Search for prompts, topics and people') : 'Search'"
        wrapper-class="bg-grayLighter text-darkGray p-2 rounded-full focus-within:ring-1 focus-within:ring-darkGray overflow-hidden h-[40px]"
        @focus="handlerFocus()"
        @reset="handlerClose()"
      >
        <template #right-addon>
          <TransitionSlide direction="right">
            <template v-if="isMobile">
              <button v-if="showFullContainer" @click="() => fieldRef?.reset()">
                <div i-mdi:close />
              </button>
            </template>
            <template v-else>
              <IconArrow
                v-if="showFullContainer"
                :background-color="colors.softGray"
                :stroke-color="colors.bluishSofter"
                border="~ softGray" rounded-full
                transform rotate="90deg"
                w-6 h-6
              />

              <div v-else class="hidden md:flex items-center gap-.5 text-xs border-1 text-darkGray border-softGray p-1 rounded-1 ">
                <IconCommand h-3 w-3 />
                <span role="img" class="capitalize">
                  k
                </span>
              </div>
            </template>
          </TransitionSlide>
        </template>
      </DebouncedField>

      <div v-show="showFullContainer" flex="~ col" py-2 gap-4 h-max max-h="500px" :class="isMobile && $style.searchResult">
        <AppTabs>
          <ElementalSearchTab
            name="Prompts"
            :search="searchDebounced"
            :type="ElementalTypeEnum && ElementalTypeEnum.Prompt"
            order-by="votes"
          />

          <ElementalSearchTab
            name="Automations"
            :search="searchDebounced"
            :type="ElementalTypeEnum && ElementalTypeEnum.Automation"
            order-by="votes"
          />

          <ListSearchTab :search="searchDebounced" />
          <TopicSearchTab :search="searchDebounced" />
          <UsersSearchTab :search="searchDebounced" />
          <TeamsSearchTab v-if="teamsFeatures.enable" :search="searchDebounced" />
        </AppTabs>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.container {
  min-width: 100%;
}

.containerDesktopActive {
  @apply bg-white;
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.04);
  padding: 10px 20px 20px 20px;
  min-width: 360px;
}

.containerMobileActive {
  padding: 0;
}

.input {
  font-size: 14px;
  line-height: 16px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    border: none;
  }

  &::placeholder {
    @apply text-bluish-softer;
    font-size: 14px;
    line-height: 20px;
  }
}

.searchResult {
  @apply bg-white;
  width: 100%;
  box-shadow: 0px 10px 8px rgba(0, 0, 0, 0.04);
  width: 100%;
  position: fixed;
  top: 60px;
  left: 0;
  display: block;
  overflow: auto;
  min-height: 100%;
}
</style>
